/**
 * PR (Personal Record) Detection and Tracking Service
 *
 * Detects when a user achieves a new personal record by:
 * 1. Finding their historical best for an exercise at a given rep range
 * 2. Comparing new sets against historical bests
 * 3. Tracking PR achievements over time
 */

import { supabase } from '../supabase';
import * as Sentry from '@sentry/react';

/**
 * Get rep range category for grouping PRs
 * PRs are tracked per rep range, not per exact rep count
 *
 * @param {number} reps - Number of reps performed
 * @returns {string} - Rep range category (e.g., "1-3", "4-6", "7-10", "11-15", "16+")
 */
export const getRepRangeCategory = (reps) => {
  if (reps <= 3) return '1-3';
  if (reps <= 6) return '4-6';
  if (reps <= 10) return '7-10';
  if (reps <= 15) return '11-15';
  return '16+';
};

// ── The one PR decision ─────────────────────────────────────────────────────────────────────
// There used to be two definitions of "a PR" in the app: this rep-range-bucketed one, and a
// VOLUME-ranked duplicate inside historyService.detectSessionPRs that powered the end-of-session
// summary. They disagreed, so Stats and the workout summary could tell a client different things.
// RecordsTab already called this one "the correct" detection; the volume one has now been retired,
// and both callers share the two functions below.
//
// The split is deliberate. evaluateSetPR holds the RULES and touches no database; each caller
// gathers the two facts it needs however it can do so most cheaply — checkForPR with two targeted
// queries for one live set, detectSessionPRs from the whole-session history it already batches.

/**
 * Decides whether one set is a personal record. Pure — no queries.
 *
 * @param {Object} p
 * @param {number|string} p.weight        - Weight logged for this set
 * @param {number|string} p.reps          - Reps performed
 * @param {boolean} [p.isBodyweight]      - Bodyweight exercises are excluded entirely: the logged
 *                                          weight is bodyweight x multiplier, so it moves with body
 *                                          composition rather than performance
 * @param {boolean} [p.isAssisted]        - Assisted: LESS weight = less assistance = harder = better
 * @param {Object|null} [p.bestInRange]   - Best PRIOR set in this set's rep range ({ weight, reps,
 *                                          created_at }), or null if this is the first one there.
 *                                          Must exclude the set being judged.
 * @param {boolean} [p.matchedOrBeaten]   - Has any PRIOR set already done at least this weight for
 *                                          at least this many reps, in ANY rep range? Stops
 *                                          25kg x 10 counting as a PR when 26.25kg x 12 is on
 *                                          record, and stops the 2nd identical set of a session
 *                                          re-winning what the 1st already won.
 * @returns {{ isPR: boolean, previousPR: Object|null, improvement: number|null, isFirstEver?: boolean }}
 */
export const evaluateSetPR = ({
  weight, reps, isBodyweight = false, isAssisted = false, bestInRange = null, matchedOrBeaten = false,
}) => {
  const w = parseFloat(weight);
  const r = parseInt(reps);
  if (!w || !r) return { isPR: false, previousPR: null, improvement: null };
  if (isBodyweight) return { isPR: false, previousPR: null, improvement: null };

  if (!bestInRange) {
    // Nothing in this rep range yet — a first, unless they have already matched it elsewhere.
    if (matchedOrBeaten) return { isPR: false, previousPR: null, improvement: null };
    return { isPR: true, previousPR: null, improvement: null, isFirstEver: true };
  }

  const best = parseFloat(bestInRange.weight);
  const beatsBest = isAssisted ? w < best : w > best;
  if (!beatsBest || matchedOrBeaten) {
    return { isPR: false, previousPR: bestInRange, improvement: null };
  }
  return {
    isPR: true,
    previousPR: bestInRange,
    improvement: isAssisted ? best - w : w - best,
    isFirstEver: false,
  };
};

/**
 * Derives evaluateSetPR's two inputs from a list of prior sets. For callers that already hold the
 * history in memory; checkForPR queries for the same two facts instead.
 *
 * @param {Array<{weight: number|string, reps: number|string}>} priorSets - MUST exclude the set
 *        being judged, and should already be scoped to the same exercise.
 * @param {{weight: number|string, reps: number|string, isAssisted?: boolean}} set
 * @returns {{ bestInRange: Object|null, matchedOrBeaten: boolean }}
 */
export const summarisePriorSets = (priorSets, { weight, reps, isAssisted = false }) => {
  const w = parseFloat(weight);
  const r = parseInt(reps);
  const range = getRepRangeCategory(r);

  let bestInRange = null;
  let matchedOrBeaten = false;

  for (const p of priorSets || []) {
    const pw = parseFloat(p.weight);
    const pr = parseInt(p.reps);
    if (!Number.isFinite(pw) || !Number.isFinite(pr)) continue;

    if (getRepRangeCategory(pr) === range) {
      if (!bestInRange || (isAssisted ? pw < parseFloat(bestInRange.weight) : pw > parseFloat(bestInRange.weight))) {
        bestInRange = p;
      }
    }
    // "already done at least this hard, for at least as many reps" — in ANY rep range
    if (pr >= r && (isAssisted ? pw <= w : pw >= w)) matchedOrBeaten = true;
  }

  return { bestInRange, matchedOrBeaten };
};

/**
 * Get the user's current PR for a specific exercise and rep range
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name (from snapshot)
 * @param {string} repRange - Rep range category (e.g., "4-6")
 * @param {boolean} isAssisted - If true, PR = lowest weight (least assistance = hardest)
 * @returns {Object|null} - PR record with weight, reps, date, or null if no PR
 */
export const getCurrentPR = async (userId, exerciseName, repRange, isAssisted = false, excludeSessionId = null) => {
  try {
    const repRangeBounds = {
      '1-3': { min: 1, max: 3 },
      '4-6': { min: 4, max: 6 },
      '7-10': { min: 7, max: 10 },
      '11-15': { min: 11, max: 15 },
      '16+': { min: 16, max: 999 },
    };

    const bounds = repRangeBounds[repRange];
    if (!bounds) return null;

    // For assisted: lowest weight = least assistance = hardest = best PR
    // For regular:  highest weight = most load = best PR
    let query = supabase
      .from('workout_logs')
      .select('id, weight, reps, created_at, exercise_name')
      .eq('user_id', userId)
      .eq('exercise_name', exerciseName)
      .gte('reps', bounds.min)
      .lte('reps', bounds.max)
      .not('weight', 'is', null)
      // Only sets that were done: a weight typed but never ticked is not a record to beat (Codex's
      // task 017 review; the Stats reads were fixed the same way)
      .eq('completed', true);

    // Exclude the current session so within-session progressive sets
    // don't trigger false PR notifications
    if (excludeSessionId) {
      query = query.neq('session_id', excludeSessionId);
    }

    const { data, error } = await query
      .order('weight', { ascending: isAssisted })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;

    return data;
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getCurrentPR', supabaseError: error, userId, exerciseName, repRange } });
    console.error('Error getting current PR:', error);
    return null;
  }
};

/**
 * Check if a new set is a PR
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name
 * @param {number} weight - Weight lifted (raw value from workout_logs)
 * @param {number} reps - Reps performed
 * @param {boolean} isBodyweight - Bodyweight exercises are excluded from weight-based PRs
 *   because logged weight = userBodyweight × multiplier, which changes with body composition
 *   rather than pure performance
 * @param {boolean} isAssisted - For assisted exercises, lower weight = less assistance = harder = PR
 * @returns {Object} - { isPR: boolean, previousPR: Object|null, improvement: number|null }
 */
export const checkForPR = async (userId, exerciseName, weight, reps, isBodyweight = false, isAssisted = false, sessionId = null) => {
  try {
    // The RULES live in evaluateSetPR, shared with historyService.detectSessionPRs so the live
    // badge, the Stats screens and the end-of-session summary cannot drift apart again. This
    // function's only job is to fetch the two facts that decision needs, as cheaply as possible
    // for a single set logged mid-workout.
    if (!weight || !reps || isBodyweight) {
      return evaluateSetPR({ weight, reps, isBodyweight, isAssisted });
    }

    const newWeight = parseFloat(weight);
    const repRange = getRepRangeCategory(reps);

    // Fact 1: the best prior set in this rep range. Excludes the current session so progressive
    // loading within one workout (warm-ups, then the top set) does not fire a false PR.
    const bestInRange = await getCurrentPR(userId, exerciseName, repRange, isAssisted, sessionId);

    // Fact 2 is only ever consulted when the set is otherwise winning, so skip the query when it
    // cannot change the answer — this runs live after every logged set.
    const couldWin = !bestInRange
      || (isAssisted ? newWeight < parseFloat(bestInRange.weight) : newWeight > parseFloat(bestInRange.weight));

    let matchedOrBeaten = false;
    if (couldWin) {
      // Has any set already done at least this weight for at least this many reps, in ANY rep
      // range? limit(2): the set being judged is already saved, so it always matches once —
      // 2+ means some OTHER set has already demonstrated this.
      const q = supabase
        .from('workout_logs')
        .select('id')
        .eq('user_id', userId)
        .eq('exercise_name', exerciseName)
        .gte('reps', reps)
        .not('weight', 'is', null)
        .eq('completed', true)
        .limit(2);
      const { data: qualifying } = await (isAssisted ? q.lte('weight', newWeight) : q.gte('weight', newWeight));
      matchedOrBeaten = (qualifying || []).length >= 2;
    }

    return evaluateSetPR({ weight, reps, isBodyweight, isAssisted, bestInRange, matchedOrBeaten });
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'checkForPR', supabaseError: error, userId, exerciseName, weight, reps } });
    console.error('Error checking for PR:', error);
    return { isPR: false, previousPR: null, improvement: null };
  }
};

/**
 * Get all PRs for a user (across all exercises)
 * Groups by exercise and rep range
 *
 * @param {string} userId - User's auth ID
 * @returns {Array} - Array of PR records grouped by exercise and rep range
 */
export const getAllUserPRs = async (userId, { strict = false } = {}) => {
  try {
    // Fetch logs including exercise_id so we can look up is_bodyweight / is_assisted.
    // Must filter completed=true — skipped/incomplete sets must not count as PRs.
    const { data: logs, error } = await supabase
      .from('workout_logs')
      .select('exercise_id, exercise_name, exercise_type, movement_category, weight, reps, created_at')
      .eq('user_id', userId)
      .eq('completed', true)
      .not('weight', 'is', null)
      .not('reps', 'is', null)
      .order('created_at', { ascending: false })
      .limit(10000);

    if (error) throw error;
    if (!logs || logs.length === 0) return [];

    // Fetch exercise flags for all distinct exercise IDs
    const exerciseIds = [...new Set(logs.map(l => l.exercise_id).filter(Boolean))];
    let exerciseMap = new Map();
    if (exerciseIds.length > 0) {
      const { data: exercises } = await supabase
        .from('exercises')
        .select('id, is_bodyweight, is_assisted')
        .in('id', exerciseIds);
      exerciseMap = new Map((exercises || []).map(e => [e.id, e]));
    }

    // Group by exercise + rep range, tracking best performance per type
    const prMap = new Map();

    logs.forEach(log => {
      const exercise = exerciseMap.get(log.exercise_id);
      const isBodyweight = exercise?.is_bodyweight || false;
      const isAssisted = exercise?.is_assisted || false;

      // Bodyweight PRs are excluded: logged weight changes with body composition,
      // not just with performance improvement
      if (isBodyweight) return;

      const repRange = getRepRangeCategory(log.reps);
      const key = `${log.exercise_name}|${repRange}`;
      const existing = prMap.get(key);
      const logWeight = parseFloat(log.weight);

      // Assisted: lower weight = less assistance = harder = better PR
      // Regular:  higher weight = more load = better PR
      const isBetter = !existing || (
        isAssisted
          ? logWeight < parseFloat(existing.weight)
          : logWeight > parseFloat(existing.weight)
      );

      if (isBetter) {
        prMap.set(key, {
          exercise_name: log.exercise_name,
          exercise_type: log.exercise_type,
          movement_category: log.movement_category,
          is_assisted: isAssisted,
          rep_range: repRange,
          weight: log.weight,
          reps: log.reps,
          created_at: log.created_at,
        });
      }
    });

    return Array.from(prMap.values()).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getAllUserPRs', supabaseError: error, userId } });
    console.error('Error getting all PRs:', error);
    // Stats says it could not load, rather than "no records yet" (Codex's task 017 review)
    if (strict) throw error;
    return [];
  }
};

/**
 * Get recent PRs (last 30 days)
 *
 * @param {string} userId - User's auth ID
 * @param {number} days - Number of days to look back (default 30)
 * @returns {Array} - Array of recent PR achievements
 */
// The records set in the last N days, from the full list: a screen that has the full list needs no
// second read (Codex's task 017 review counted the same records read twice)
export const recentOf = (prs, days = 30, now = new Date()) => {
  const cutoffDate = new Date(now);
  cutoffDate.setDate(cutoffDate.getDate() - days);
  return prs.filter(pr => new Date(pr.created_at) >= cutoffDate);
};

export const getRecentPRs = async (userId, days = 30) => {
  try {
    return recentOf(await getAllUserPRs(userId), days);
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getRecentPRs', supabaseError: error, userId, days } });
    console.error('Error getting recent PRs:', error);
    return [];
  }
};

/**
 * Get PR progression for a specific exercise
 * Shows how the PR has improved over time
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name
 * @param {string} repRange - Rep range category (optional, gets all if not specified)
 * @param {boolean} isAssisted - If true, progression tracks decreasing assistance weight
 * @returns {Array} - Array of PR milestones over time
 */
export const getPRProgression = async (userId, exerciseName, repRange = null, isAssisted = false, { strict = false } = {}) => {
  try {
    let query = supabase
      .from('workout_logs')
      .select('weight, reps, created_at')
      .eq('user_id', userId)
      .eq('completed', true) // finished sets only (task 017, M1)
      .eq('exercise_name', exerciseName)
      .not('weight', 'is', null)
      .not('reps', 'is', null)
      .order('created_at', { ascending: true });

    if (repRange) {
      const repRangeBounds = {
        '1-3': { min: 1, max: 3 },
        '4-6': { min: 4, max: 6 },
        '7-10': { min: 7, max: 10 },
        '11-15': { min: 11, max: 15 },
        '16+': { min: 16, max: 999 },
      };

      const bounds = repRangeBounds[repRange];
      if (bounds) {
        query = query.gte('reps', bounds.min).lte('reps', bounds.max);
      }
    }

    const { data: logs, error } = await query;

    if (error) throw error;
    if (!logs || logs.length === 0) return [];

    // For regular exercises: track new highs (increasing weight = PR)
    // For assisted exercises: track new lows (decreasing assistance = PR)
    const progression = [];
    let currentBest = isAssisted ? Infinity : 0;

    logs.forEach(log => {
      const weight = parseFloat(log.weight);
      const isPR = isAssisted ? weight < currentBest : weight > currentBest;

      if (isPR) {
        const improvement = isAssisted
          ? (currentBest === Infinity ? null : currentBest - weight)
          : (currentBest > 0 ? weight - currentBest : null);

        progression.push({
          weight: log.weight,
          reps: log.reps,
          created_at: log.created_at,
          improvement,
        });
        currentBest = weight;
      }
    });

    return progression;
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getPRProgression', supabaseError: error, userId, exerciseName, repRange } });
    console.error('Error getting PR progression:', error);
    if (strict) throw error;
    return [];
  }
};

/**
 * Get PR count by muscle group
 * Useful for stats dashboard
 *
 * @param {string} userId - User's auth ID
 * @returns {Object} - Object with muscle groups as keys and PR counts as values
 */
export const getPRCountByMuscleGroup = async (userId) => {
  try {
    const allPRs = await getAllUserPRs(userId);

    const countByGroup = {};

    allPRs.forEach(pr => {
      const group = pr.movement_category || 'Other';
      countByGroup[group] = (countByGroup[group] || 0) + 1;
    });

    return countByGroup;
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getPRCountByMuscleGroup', supabaseError: error, userId } });
    console.error('Error getting PR count by muscle group:', error);
    return {};
  }
};
